import React, { useState, useEffect } from "react"
import {
  FileText, Loader2, Sparkles, Package, Calendar,
  AlertCircle, Tag, ChevronLeft, Truck, MoreVertical, Trash2, AlertTriangle,
  ChevronDown, ChevronUp
} from "lucide-react"
import { useNavigate } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import { useGetRfqBuyerToAdminWithQuotations } from "@/hooks/buyer/useGetRfqBuyerToAdminWithQuotations"
import { useGetRfqBuyerToAdminStatusUpdates } from "@/hooks/buyer/useGetRfqBuyerToAdminStatusUpdates"
import { useCancelRfqBuyerToAdmin } from "@/hooks/buyer/useCancelRfqBuyerToAdmin"
import { CompanyDetailSidebar, ProductDetailSidebar, VariantDetailSidebar, ExternalSellerDetailSidebar } from "@/components/dashboard/shared/SourcingDetailSidebars"
import { TargetItemView } from "@/components/dashboard/shared/TargetItemView"
import { RfqStatusUpdatesTimeline } from "@/components/dashboard/shared/RfqStatusUpdatesTimeline"
import { QuotationDetailsPage } from "@/components/dashboard/buyer/pages/quotations/QuotationDetailsPage"
import { PromptModalCard } from "@/components/shared/PromptModalCard"
import { toast } from "sonner"

// Helper to format date
function formatDate(isoStr: string | null | undefined) {
  if (!isoStr) return "-"
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return "-"
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export function RfqDetailsPage({ rfqId }: { rfqId: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: "/rfqs" })
    }
  }

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
    refetch: refetchDetail,
  } = useGetRfqBuyerToAdminWithQuotations(rfqId)

  const {
    data: statusUpdatesData,
    isLoading: isStatusUpdatesLoading,
    refetch: refetchStatusUpdates,
  } = useGetRfqBuyerToAdminStatusUpdates(rfqId)

  const { mutate: cancelRfq, isPending: isCanceling } = useCancelRfqBuyerToAdmin()
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = useState<any | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null)
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [isTargetsExpanded, setIsTargetsExpanded] = useState(false)

  // Hide scrollbar on scroll container to prevent horizontal layout shifts when expanding content
  useEffect(() => {
    const mainEl = document.querySelector("main")
    if (mainEl) {
      mainEl.classList.add("scrollbar-none")
      return () => {
        mainEl.classList.remove("scrollbar-none")
      }
    }
  }, [])

  if (isDetailLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-[#be001e]/20 rounded-2xl shadow-sm max-w-5xl mx-auto font-sans text-left">
        <button
          onClick={handleGoBack}
          className="absolute top-4 left-4 flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
        >
          <ChevronLeft className="size-4" />
        </button>
        <Loader2 className="size-8 animate-spin text-[#e6202f]" />
        <p className="text-sm font-medium text-slate-500">Fetching RFQ details...</p>
      </div>
    )
  }

  if (isDetailError) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left animate-in fade-in duration-200">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-[#e6202f]">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">{(detailError as Error)?.message || "Failed to load RFQ details."}</p>
        </div>
      </div>
    )
  }

  const selectedRfq = detailData?.buyerToAdminRfq
  if (!selectedRfq) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left animate-in fade-in duration-200">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-5 text-sm text-yellow-700">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">RFQ details not found.</p>
        </div>
      </div>
    )
  }

  const rfqStatus = (selectedRfq.status || "").toUpperCase()
  const validUntilDate = selectedRfq.validUntil ? new Date(selectedRfq.validUntil) : null
  const isExpired = rfqStatus === "OPEN" && validUntilDate ? validUntilDate < new Date() : false
  const resolvedStatus = isExpired ? "EXPIRED" : (rfqStatus === "CLOSE" ? "CLOSED" : rfqStatus || "OPEN")

  const hasSpecs = 
    (selectedRfq.quantity !== null && selectedRfq.quantity !== undefined) ||
    selectedRfq.budget ||
    selectedRfq.moq ||
    selectedRfq.paymentTerms ||
    selectedRfq.estimatedTime ||
    selectedRfq.country ||
    selectedRfq.address

  const allQuotations = selectedRfq.quotations || []
  const quotations = allQuotations.filter((q: any) => q.isLatest === true)

  return (
    <div className="space-y-6 pb-24 sm:pb-28">
      <div className="animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto">
        <div className="group relative overflow-hidden bg-white border border-[#be001e]/20 hover:border-[#be001e]/30 rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-[0_20px_50px_rgba(230,32,47,0.06)] transition-all duration-300">
          {/* Top Accent Glow */}
          <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-[#be001e] to-[#e6202f] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
          
          {/* Header Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleGoBack}
                className="flex items-center gap-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer p-1"
              >
                <ChevronLeft className="size-4" />
              </button>
                <span className="text-sm text-slate-700 font-medium ">Back to RFQs</span>
            </div>

            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-0.5 rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider ${
                resolvedStatus === "OPEN"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                  : (resolvedStatus === "PENDING" || resolvedStatus === "REQUEST_REVISION")
                  ? "bg-amber-50 text-amber-700 border-amber-100"
                  : (resolvedStatus === "EXPIRED" || resolvedStatus === "CANCELLED" || resolvedStatus === "REJECTED")
                  ? "bg-red-50 text-red-700 border-red-100"
                  : resolvedStatus === "QUOTED"
                  ? "bg-blue-50 text-blue-700 border-blue-100"
                  : resolvedStatus === "FORWARDED"
                  ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                  : "bg-slate-50 text-slate-700 border-slate-200"
              }`}>
                {resolvedStatus === "REQUEST_REVISION" ? "REQUEST REVISION" : resolvedStatus}
              </span>
              {(resolvedStatus === "OPEN" || resolvedStatus === "FORWARDED") && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="p-1.5 text-slate-500 hover:text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/30 rounded-lg transition-all border border-slate-200 cursor-pointer flex items-center justify-center"
                    title="More actions"
                  >
                    <MoreVertical className="size-4" />
                  </button>
                  {showDropdown && (
                    <>
                      <div 
                        className="fixed inset-0 z-30" 
                        onClick={() => setShowDropdown(false)} 
                      />
                      <div className="absolute right-0 mt-1 w-36 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => {
                            setShowDropdown(false)
                            setShowCancelModal(true)
                          }}
                          disabled={isCanceling}
                          className="w-full text-left px-3 py-2 text-xs font-bold hover:bg-red-50 transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="size-3.5" />
                          Cancel Request
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-[#be001e]/10 w-full" />

          {/* Grid Layout inside the card */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Column (span 3): Message & Targets */}
            <div className="lg:col-span-3 space-y-5">
              {/* Sourcing Target */}
              {selectedRfq.targets && selectedRfq.targets.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <Sparkles className="size-3.5 text-[#e6202f]" /> Sourcing Target
                      {selectedRfq.targets.length > 1 && (
                        <span className="text-[10px] text-slate-400 font-semibold lowercase">
                          ({selectedRfq.targets.length})
                        </span>
                      )}
                    </h4>

                    {selectedRfq.targets.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setIsTargetsExpanded((prev) => !prev)}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-[#be001e] hover:text-[#be001e]/80 transition-colors cursor-pointer"
                      >
                        {isTargetsExpanded ? (
                          <>
                            Show less <ChevronUp className="size-3.5 stroke-[2.5]" />
                          </>
                        ) : (
                          <>
                            Show all ({selectedRfq.targets.length}) <ChevronDown className="size-3.5 stroke-[2.5]" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {(isTargetsExpanded ? selectedRfq.targets : selectedRfq.targets.slice(0, 2)).map((target: any) => (
                      <TargetItemView key={target.id} target={target} onCompanyClick={setSelectedCompany} onVariantClick={setSelectedVariant} onProductClick={setSelectedProduct} onExternalSellerClick={setSelectedExternalSeller} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Procurement Request */}
              {selectedRfq.queryMessage && (
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <FileText className="size-3.5 text-[#e6202f]" /> Procurement Request
                  </h4>
                  <p className="text-sm text-slate-700 font-medium bg-[#be001e]/[0.01] p-3.5 rounded-xl border border-[#be001e]/15 hover:border-[#be001e]/25 transition-colors leading-relaxed italic">
                    {selectedRfq.queryMessage}
                  </p>
                </div>
              )}

              {/* Sourcing Specifications / Parameters */}
              {hasSpecs && (
                <div className="space-y-2">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Tag className="size-3.5 text-[#e6202f]" /> Sourcing Specifications
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50/50 border border-slate-200/50 p-4 rounded-xl">
                    {/* Quantity */}
                    {selectedRfq.quantity && (
                      <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                          <Tag className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Quantity</p>
                          <p className="text-xs font-bold text-slate-750">{selectedRfq.quantity + " " + selectedRfq.measureUnit}</p>
                        </div>
                      </div>
                    )}

                    {/* Budget */}
                    {selectedRfq.budget && (
                      <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                          <Tag className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Target Budget</p>
                          <p className="text-xs font-bold text-slate-750">{selectedRfq.currencyCode + " " + selectedRfq.budget}</p>
                        </div>
                      </div>
                    )}

                    {/* Payment Terms */}
                    {selectedRfq.paymentTerms && (
                      <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                          <FileText className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Payment Terms</p>
                          <p className="text-xs font-bold text-slate-750">{selectedRfq.paymentTerms}</p>
                        </div>
                      </div>
                    )}

                    {/* Lead time */}
                    {selectedRfq.leadTime && (
                      <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                          <Tag className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Lead Time</p>
                          <p className="text-xs font-bold text-slate-750">{formatDate(selectedRfq.leadTime)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Logistics Specifications */}
              {(selectedRfq.preferredShipmentMode || selectedRfq.shipmentType) && (
                <div className="space-y-2">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Truck className="size-3.5 text-[#e6202f]" /> Logistics Specifications
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50/50 border border-slate-200/50 p-4 rounded-xl">
                    {/* Preferred Shipment Mode */}
                    {selectedRfq.preferredShipmentMode && (
                      <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                          <Truck className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Shipment Mode</p>
                          <p className="text-xs font-bold text-slate-750">{selectedRfq.preferredShipmentMode}</p>
                        </div>
                      </div>
                    )}

                    {/* Shipment Type */}
                    {selectedRfq.shipmentType && (
                      <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-[#be001e]/10 transition-colors">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#be001e]/5 text-[#e6202f]">
                          <Package className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Shipment Type</p>
                          <p className="text-xs font-bold text-slate-750">{selectedRfq.shipmentType.replace(/_/g, " ")}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column (span 1): Timestamps / Status Updates */}
            <RfqStatusUpdatesTimeline
              isLoading={isStatusUpdatesLoading}
              checkpoints={statusUpdatesData?.checkpoints}
              createdAt={selectedRfq.createdAt}
              validUntil={selectedRfq.validUntil}
              createdLabel="SENT DATE"
            />
          </div>
        </div>
      </div>

      {/* Render latest quotation card per supplier */}
      {quotations.length > 0 && (
        <div className="space-y-6">
          {quotations.map((quotation: any, index: number) => (
            <QuotationDetailsPage
              key={quotation.id || index}
              quotation={quotation}
              showBack={false}
              quotationIndex={index}
              totalQuotations={quotations.length}
              allQuotations={allQuotations}
            />
          ))}
        </div>
      )}

      {/* Empty space below quotation card for mobile scroll clearance so the chatbot avatar doesn't overlap */}
      <div className="h-20 sm:h-24 w-full pointer-events-none" aria-hidden="true" />

      {/* Slide-over Detail Sidebars */}
      <CompanyDetailSidebar 
        company={selectedCompany} 
        onClose={() => setSelectedCompany(null)} 
        level={selectedProduct || selectedVariant || selectedVariantId ? 1 : 0}
      />
      <ExternalSellerDetailSidebar 
        externalSeller={selectedExternalSeller} 
        onClose={() => setSelectedExternalSeller(null)} 
      />
      {(selectedVariant || selectedVariantId) && (
        <VariantDetailSidebar
          variant={selectedVariant || undefined}
          variantId={selectedVariantId || undefined}
          onClose={() => {
            setSelectedVariant(null)
            setSelectedVariantId(null)
          }}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}
      {selectedProduct && (
        <ProductDetailSidebar
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}

      {showCancelModal && (
        <PromptModalCard
          title="Cancel RFQ Request?"
          description="Are you sure you want to cancel this RFQ? Suppliers will no longer be able to submit quotes for this request."
          icon={AlertTriangle}
          iconContainerClassName="bg-red-50 text-red-600"
          iconClassName="size-6"
          primaryAction={{
            label: "Keep RFQ",
            className: "flex-1 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer",
            onClick: () => setShowCancelModal(false)
          }}
          secondaryAction={{
            label: isCanceling ? "Canceling..." : "Cancel RFQ",
            disabled: isCanceling,
            className: "flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer shadow-sm disabled:opacity-50",
            onClick: () => {
              cancelRfq(selectedRfq.id, {
                onSuccess: () => {
                  toast.success("RFQ Cancelled Successfully")
                  setShowCancelModal(false)
                  refetchDetail()
                  refetchStatusUpdates()
                  queryClient.invalidateQueries()
                },
                onError: () => {
                  // Handled globally by http.ts response interceptor
                }
              })
            }
          }}
          onClose={() => setShowCancelModal(false)}
          onCloseText=""
        />
      )}
    </div>
  )
}
